/**
 * NikahElegant — Muslim Nikah ceremony (elegant, calligraphy-inspired).
 *
 * Fully data-driven, with a structured flow: Hero → Families → Ceremony → Walima → RSVP.
 * Each section is a reusable component mapping `NikahContent` fields.
 * Self-registers with the template registry.
 *
 * Registry ID `nikah-elegant-experience` MUST match the migration and StepTemplate hook.
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { nikahTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { RsvpSection } from './sections/RsvpSection';

const META: TemplateMetadata = {
  id: 'nikah-elegant-experience',
  name: 'NikahElegant — Elegant Muslim Wedding',
  occasionType: 'WEDDING',
  description:
    'A refined Muslim Nikah invitation: Bismillah header, animated geometric mandala, family details, ceremony & walima events, and a clear RSVP.',
  isPremium: true,
  tags: ['nikah', 'muslim', 'elegant', 'arabic', 'animated', 'gold'],
  colorScheme: {
    primary: nikahTheme.emerald,
    accent: nikahTheme.gold,
    bg: nikahTheme.ivory,
  },
};

export function NikahElegantTemplate({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  const scrollToRsvp = React.useCallback(() => {
    document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main
      className="nikah-root min-h-screen scroll-smooth bg-[var(--nikah-ivory)] font-sans text-[var(--nikah-ink)] antialiased"
      style={themeCssVars(nikahTheme)}
    >n      <HeroSection content={content.hero} onRsvp={scrollToRsvp} />

      {/* Temporary placeholder for other sections that will be added soon */}
      <section className="px-6 py-12">
        <h2>Coming soon: Families, Ceremony, Walima sections...</h2>
      </section>

      <RsvpSection
        content={content.rsvp}
        invitationId={invitationId}
        isLive={isLive}
      />
    </main>
  );
}

registry.register({ metadata: META, component: NikahElegantTemplate });